/** Preflight status presentation vocabulary — shared by the Preflight tab, the
 *  Items drawer, the tour-operator Setup wizard and the Offboarding tab, so a status
 *  never renders two different colors on two surfaces. */
import type { ChipProps } from '@mui/material';

import type { PreflightStatus } from '@/api/preflight';

/** The statuses ops may set by hand (PATCH) — the status control's options. `skipped`
 *  (Skip…, with a reason) and `superseded` (set by a reopen) are never written that way. */
export const PREFLIGHT_STATUSES: PreflightStatus[] = ['pending', 'started', 'completed', 'error'];

/** Every status an offboarding item can have, in the order its counts read. */
export const OFFBOARDING_STATUSES: PreflightStatus[] = [...PREFLIGHT_STATUSES, 'skipped', 'superseded'];

export const STATUS_LABELS: Record<PreflightStatus, string> = {
  pending: 'Pending',
  started: 'Started',
  completed: 'Completed',
  error: 'Error',
  skipped: 'Skipped',
  superseded: 'Superseded',
};

/** Work still to do: not completed, nor skipped or superseded (offboarding). On an
 *  onboarding item this is exactly "not completed". */
export function isOpenStatus(status: PreflightStatus): boolean {
  return status === 'pending' || status === 'started' || status === 'error';
}

export function statusChipColor(status: PreflightStatus): ChipProps['color'] {
  switch (status) {
    case 'pending': return 'default';
    case 'started': return 'warning';
    case 'completed': return 'success';
    case 'error': return 'error';
    // A decision recorded with a reason: settled, but not the work done.
    case 'skipped': return 'info';
    // Overtaken by a reopen: inert, nothing to do.
    case 'superseded': return 'default';
    default: return 'default';
  }
}

/** Theme palette color token per status, for coloring text/icons via sx `color`. */
export function statusColor(status: PreflightStatus): string {
  switch (status) {
    case 'started': return 'warning.main';
    case 'completed': return 'success.main';
    case 'error': return 'error.main';
    case 'skipped': return 'info.dark';
    default: return 'text.secondary'; // pending, superseded
  }
}
